import type { JSX, ReactNode } from 'react'

import { dioptre, formatDioptre, type PrescriptionStatus } from '@lensmira/domain'
import { formatDay } from '@lensmira/i18n'

import { Ltr, useTranslation, type Translation } from '../i18n.js'
import { sentence } from '../sentence.js'
import { rxStatusLabel } from '../status-labels.js'

/**
 * Contact lens prescriptions on the customer profile. §5.2, `RX-001`, `RX-002`.
 *
 * A separate record type with its own section, not a row among the spectacle ones: base curve,
 * diameter, brand and modality have no spectacle equivalent. Reads only; the actions are a slot
 * because they are writes and this screen makes none (`OFF-001a`).
 */

export interface ContactLensEyeView {
  readonly power: number
  readonly cylinder?: number
  readonly axis?: number
  readonly add?: number
  readonly baseCurveMm: number
  readonly diameterMm: number
  readonly brand: string
  readonly material?: string
  readonly modality: string
  readonly wearingSchedule?: string
  /** Absent means no policy has been set, which is not the same as allowed. */
  readonly brandSubstitutionAllowed?: boolean
}

export interface ContactLensPrescriptionView {
  readonly id: string
  readonly version: number
  readonly status: PrescriptionStatus
  /** ISO `yyyy-mm-dd`. */
  readonly issueDate: string
  readonly expiryDate?: string
  readonly fittingDate?: string
  readonly aftercareDueDate?: string
  readonly prescriberName?: string
  readonly supersedes?: string
  readonly supersededBy?: string
  readonly supersedeReason?: string
  readonly od: ContactLensEyeView
  readonly os: ContactLensEyeView
}

type T = Translation['t']

function formatPower(value: number | undefined, t: T): string {
  if (value === undefined) return t('common.notRecorded')
  try {
    return formatDioptre(dioptre(value))
  } catch {
    return String(value)
  }
}

const MODALITY_KEY: Readonly<Record<string, string>> = {
  daily: 'clRx.modality.daily',
  two_weekly: 'clRx.modality.twoWeekly',
  monthly: 'clRx.modality.monthly',
  quarterly: 'clRx.modality.quarterly',
  annual: 'clRx.modality.annual',
}

const modalityLabel = (t: T, value: string): string => {
  const key = MODALITY_KEY[value]
  return key === undefined ? value : t(key as Parameters<T>[0])
}

function substitutionLine(t: T, eye: ContactLensEyeView): string {
  if (eye.brandSubstitutionAllowed === undefined) return t('customerDetail.cl.substitution.unset')
  return eye.brandSubstitutionAllowed
    ? t('customerDetail.cl.substitution.allowed')
    : t('customerDetail.cl.substitution.notAllowed')
}

export interface ContactLensSectionProps {
  /** Absent for a caller Appendix B denies `Prescription - view`. Absent is not none. */
  readonly versions: readonly ContactLensPrescriptionView[] | undefined
  readonly timezone: string
  readonly renderActions?: (rx: ContactLensPrescriptionView) => ReactNode
}

export function ContactLensSection({
  versions,
  timezone,
  renderActions,
}: ContactLensSectionProps): JSX.Element {
  const { t, locale } = useTranslation()
  const day = (value: string): string => formatDay(value, timezone, locale)

  return (
    <div>
      <h2 className="lm-screen__subheading">{t('customerDetail.cl.heading')}</h2>
      {versions === undefined ? (
        <p className="lm-hint">{t('customerDetail.rx.withheld')}</p>
      ) : versions.length === 0 ? (
        <p className="lm-hint">{t('customerDetail.cl.none')}</p>
      ) : (
        <ul className="lm-rx-history">
          {versions.map((rx) => {
            const issued = <Ltr>{day(rx.issueDate)}</Ltr>
            return (
              <li key={rx.id}>
                <article
                  id={`cl-rx-version-${rx.id}`}
                  className={
                    rx.status === 'active'
                      ? 'lm-rx-version lm-rx-version--current'
                      : 'lm-rx-version'
                  }
                  aria-labelledby={`cl-rx-version-title-${rx.id}`}
                >
                  <div className="lm-screen__actions">
                    <span id={`cl-rx-version-title-${rx.id}`}>
                      {t('customerDetail.rx.versionHeading', { version: rx.version })}
                    </span>
                    <span
                      className={rx.status === 'active' ? 'lm-badge lm-badge--open' : 'lm-badge'}
                    >
                      {rxStatusLabel(t, rx.status)}
                    </span>
                  </div>
                  {renderActions?.(rx)}

                  <table dir="ltr">
                    <caption className="lm-visually-hidden">
                      {t('customerDetail.cl.tableCaption', { version: rx.version })}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('rx.col.eye')}</th>
                        <th scope="col">{t('clRx.col.power')}</th>
                        <th scope="col">{t('rx.col.cylinder')}</th>
                        <th scope="col">{t('rx.col.axis')}</th>
                        <th scope="col">{t('rx.col.add')}</th>
                        <th scope="col">{t('clRx.col.baseCurve')}</th>
                        <th scope="col">{t('clRx.col.diameter')}</th>
                        <th scope="col">{t('clRx.col.brand')}</th>
                        <th scope="col">{t('clRx.col.modality')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(['od', 'os'] as const).map((side) => {
                        const eye = rx[side]
                        return (
                          <tr key={side}>
                            <th scope="row">
                              {t(
                                side === 'od'
                                  ? 'customerDetail.rx.row.right'
                                  : 'customerDetail.rx.row.left',
                              )}
                            </th>
                            <td>{formatPower(eye.power, t)}</td>
                            <td>{formatPower(eye.cylinder, t)}</td>
                            <td>{eye.axis === undefined ? t('common.notRecorded') : eye.axis}</td>
                            <td>{formatPower(eye.add, t)}</td>
                            <td>{eye.baseCurveMm.toFixed(2)}</td>
                            <td>{eye.diameterMm.toFixed(1)}</td>
                            <td>{eye.brand}</td>
                            <td>{modalityLabel(t, eye.modality)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>

                  <p className="lm-hint">{sentence(t, 'customerDetail.rx.issued', { issued })}</p>
                  {rx.od.brandSubstitutionAllowed === rx.os.brandSubstitutionAllowed ? (
                    <p className="lm-hint">{substitutionLine(t, rx.od)}</p>
                  ) : (
                    (['od', 'os'] as const).map((side) => (
                      <p className="lm-hint" key={side}>
                        <span>
                          {t(
                            side === 'od'
                              ? 'customerDetail.rx.row.right'
                              : 'customerDetail.rx.row.left',
                          )}
                        </span>{' '}
                        <span>{substitutionLine(t, rx[side])}</span>
                      </p>
                    ))
                  )}
                  {rx.supersedeReason === undefined ? null : (
                    <p className="lm-hint">{rx.supersedeReason}</p>
                  )}
                </article>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
